import React, { useState } from "react";
import InputField from "./InputField";
import Button from "./Button";
import axiosClient from "../api/axiosClient";

export type LoanResult = {
  totalPayable: number;
  monthlyDue: number;
  weeklyDue?: number;
  totalInterest?: number;
};

type Props = {
  onCalculate?: (res: LoanResult) => void;
  defaultAmount?: number;
  defaultTenure?: number;
  defaultInterest?: number;
};

const LoanCalculationCard: React.FC<Props> = ({ onCalculate }) => {
  const [loanAmount, setLoanAmount] = useState("");
  const [tenure, setTenure] = useState("");
  const [interest, setInterest] = useState("");
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({
    loanAmount: "",
    tenure: "",
    interest: "",
  });

  const handleCalculate = async () => {
    const P = Number(loanAmount);
    const N = Number(tenure);
    const R = Number(interest);

    let newErrors = {
      loanAmount: loanAmount ? "" : "Loan amount is required",
      tenure: tenure ? "" : "Tenure is required",
      interest: interest ? "" : "Interest rate is required",
    };

    setErrors(newErrors);

    // ❌ Stop if any error exists
    if (newErrors.loanAmount || newErrors.tenure || newErrors.interest) {
      return;
    }

    try {
      setLoading(true);

      // 🧩 API request body
      const payload = {
        loan_amount: P,
        interest_rate: R,
        tenure_months: N,
      };

      // API call
      const { data } = await axiosClient.post(
        "/dashboard/loan-calculator",
        payload,
      );

      // ✅ Expected response:
      // {
      //   loan_amount: 500000,
      //   interest_rate: 12,
      //   tenure_months: 12,
      //   total_interest: 60000,
      //   total_amount: 560000,
      //   monthly_amount: 46667,
      //   weekly_amount: 10741
      // }

      onCalculate?.({
        totalPayable: data.total_amount,
        monthlyDue: data.monthly_amount,
        weeklyDue: data.weekly_amount,
        totalInterest: data.total_interest,
      });
    } catch (error) {
      console.error("Loan calculation failed:", error);
      onCalculate?.({ totalPayable: 0, monthlyDue: 0 });
    } finally {
      setLoading(false);
    }
  };

  const handleClear = () => {
    setLoanAmount("");
    setTenure("");
    setInterest("");
    onCalculate?.({ totalPayable: 0, monthlyDue: 0 });
  };

  return (
    <div className="light-bg-color">
      <h3 className="text-2xl font-bold mb-6 text-color  mb-4">
        Loan Calculation
      </h3>

      <div className="space-y-3">
        <InputField
          label="Loan Amount"
          type="number"
          value={loanAmount}
          onChange={(e) => {
            setLoanAmount(e.target.value);
            setErrors((prev) => ({ ...prev, loanAmount: "" }));
          }}
          placeholder="Enter amount"
          required
        />
        {errors.loanAmount && (
          <p className="text-red-500 text-xs mt-1">{errors.loanAmount}</p>
        )}

        <InputField
          label="Tenure (months)"
          type="number"
          value={tenure}
          onChange={(e) => {
            setTenure(e.target.value);
            setErrors((prev) => ({ ...prev, tenure: "" }));
          }}
          placeholder="Enter months"
          required
        />
        {errors.tenure && (
          <p className="text-red-500 text-xs mt-1">{errors.tenure}</p>
        )}

        <InputField
          label="Interest Rate (%)"
          type="number"
          value={interest}
          onChange={(e) => {
            setInterest(e.target.value);
            setErrors((prev) => ({ ...prev, interest: "" }));
          }}
          placeholder="Enter interest rate"
          required
        />
        {errors.interest && (
          <p className="text-red-500 text-xs mt-1">{errors.interest}</p>
        )}

        <div className="flex gap-2">
          <Button
            onClick={handleClear}
            fullWidth
            variant="secondary"
            disabled={loading}
          >
            Clear
          </Button>
          <Button
            onClick={handleCalculate}
            fullWidth
            variant="primary"
            disabled={loading}
          >
            {loading ? "Calculating..." : "Calculate"}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default LoanCalculationCard;
